import { CAMPAIGN_TYPES } from 'containers/AwarenessCenter/constants'

import {
  AUTOMATED_LEVEL_BASED_TRAINING,
  isCustomSourceFilter,
  TRAINING_VIDEO_TYPES,
} from '../steps/TrainingModuleStep/constants'

export const PLAYLIST_MODES = {
  SINGLE: 'single',
  PLAYLIST: 'playlist',
}

export const TRAINING_PLAYLIST_MAX_VIDEOS = 20

// A draft already in Playlist mode keeps the toggle on Upload, so it can go back to Single.
export const shouldShowPlaylistToggle = ({ campaignType, sourceFilter, isPlaylist }) =>
  campaignType === CAMPAIGN_TYPES.TRAINING && (isPlaylist || !isCustomSourceFilter(sourceFilter))

const isPlaylistVideo = (video) => video?.type === TRAINING_VIDEO_TYPES.VIDEO.value

const canJoinPlaylist = (selected, video) =>
  isPlaylistVideo(video) &&
  !!video.hasPermissions &&
  selected.length < TRAINING_PLAYLIST_MAX_VIDEOS &&
  !selected.some((item) => item.id === video.id)

export const togglePlaylistVideo = (selected, video) =>
  selected.some((item) => item.id === video.id)
    ? selected.filter((item) => item.id !== video.id)
    : canJoinPlaylist(selected, video)
      ? [...selected, video]
      : selected

export const movePlaylistVideo = (selected, from, to) => {
  if (from === to || from >= selected.length) return selected

  const next = [...selected]
  next.splice(to, 0, next.splice(from, 1)[0])
  return next
}

// otherModeSelection holds the idle mode's picks: re-entering a mode restores them, else carries over what fits.
export const switchPlaylistMode = (campaign, nextMode) => {
  if (nextMode === campaign.playlistMode) return campaign

  const { selectedTrainingVideo, otherModeSelection } = campaign
  const firstLive = selectedTrainingVideo.find(({ is_deleted: isDeleted }) => !isDeleted)
  const carriedOver =
    nextMode === PLAYLIST_MODES.PLAYLIST
      ? selectedTrainingVideo.filter(isPlaylistVideo)
      : firstLive
        ? [firstLive]
        : []

  return {
    ...campaign,
    playlistMode: nextMode,
    selectedTrainingVideo: otherModeSelection.length ? otherModeSelection : carriedOver,
    otherModeSelection: selectedTrainingVideo,
  }
}

// One definition for "cannot be sent or played": a withdrawn slot, or one with nothing to play.
export const isUnavailableSlot = (video) => !!video.is_deleted || !video.items?.length

export const hasUnavailableSlot = (selected, isPlaylist) =>
  isPlaylist && selected.some(isUnavailableSlot)

export const UNAVAILABLE_BLOCK_MESSAGE = 'Remove unavailable videos to continue.'

export const getTrainingIds = (selected) =>
  selected
    .filter(({ is_deleted: isDeleted }) => !isDeleted)
    .map(({ id }) => id)
    .filter((id) => id !== AUTOMATED_LEVEL_BASED_TRAINING.id)

// Single-video requests stay identical to today's: the param exists only for a playlist.
export const buildTrainingIdsParam = (selected) => {
  const ids = [...new Set(getTrainingIds(selected))]

  return ids.length > 1 ? ids.join(',') : null
}

export const isPlaylistSelection = (selected) => buildTrainingIdsParam(selected) !== null

export const getPlaylistFieldError = (response) => {
  const errors = response?.trainingIds
  // DRF reports a bad list item as {"<index>": [message]}
  const messages = Array.isArray(errors) ? errors : Object.values(errors || {}).flat()

  return messages.length ? String(messages[0]) : null
}

export const getVideosMissingLocale = (videos = [], localeId) =>
  videos.filter(({ items = [] }) => !items.some((item) => item.localeId === localeId))

// Reminders still equal to the previous default were never edited, so they follow the new default.
export const replaceUneditedReminders = (
  reminders = [],
  previousDefaults = {},
  nextDefaults = {}
) =>
  reminders.map((reminder) => {
    const language = Object.keys(previousDefaults).find(
      (key) =>
        previousDefaults[key].subject === reminder?.subject &&
        previousDefaults[key].body === reminder?.body
    )

    return language && nextDefaults[language] ? nextDefaults[language] : reminder
  })

// An assignment body still equal to the previous default was never edited, so it follows the new one.
export const replaceUneditedMessage = (message, previousDefault, nextDefault) =>
  previousDefault && nextDefault && message === previousDefault ? nextDefault : message
